import { useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { Button, Search } from 'components/common';
import { DropdownSelectable } from 'components/common/dropdown';

type AvailableStatus =
  | 'all'
  | 'available'
  | 'added'
  | 'waiting'
  | 'owner'
  | 'inprogress'
  | 'ended';

const CalendarEventFilters = () => {
  const { messages, user } = useReduxState();

  const router = useRouter();

  const { data } = useApiData({
    allowUnauthenticatedUser: true,
    url: '/admin/categories-calendar-events'
  });

  const [viewWasCalendar, setViewWasCalendar] = useState(false);

  const handleCategory = (category: string) => {
    router.replace({ query: { ...router.query, page: 1, category } });
  };

  const handleEventStatus = (status: AvailableStatus) => {
    router.replace({ query: { ...router.query, page: 1, status } });
  };

  const handleSearch = ({ search }) => {
    if (search && router.query.view !== 'list') {
      setViewWasCalendar(true);

      router.replace({
        query: { ...router.query, page: 1, search, view: 'list' }
      });
    } else if (!search && viewWasCalendar) {
      setViewWasCalendar(false);

      delete router.query.search;
      router.replace({
        query: { ...router.query, page: 1, view: 'calendar', type: 'current' }
      });
    } else {
      if (search)
        router.replace({ query: { ...router.query, page: 1, search } });
      else {
        delete router.query.search;
        router.replace({ query: { ...router.query, page: 1 } });
      }
    }
  };

  const handleTimeFrame = (value: 'current' | 'past') => {
    router.replace({
      query: {
        ...router.query,
        category: 'all',
        page: 1,
        status: 'all',
        type: value
      }
    });
  };

  const handleView = (value: 'calendar' | 'list') => {
    setViewWasCalendar(false);
    router.replace({
      query: {
        ...router.query,
        page: 1,
        view: value,
        status: 'all',
        ...(value === 'calendar' ? { type: 'current' } : {})
      }
    });
  };

  const userHasRights =
    user?.roles.includes('superadmin') ||
    user?.permissions.includes('create-events');

  const statusOptions: { text: string; value: AvailableStatus }[] = [
    { text: messages.commonAll, value: 'all' },
    { text: messages.eventCalendarMyEvents, value: 'added' },
    {
      text: messages.eventCalendarParticipantWaitingStatus,
      value: 'waiting'
    }
  ];

  if (router.query.type !== 'past') {
    statusOptions.push(
      { text: messages.commonInProgress, value: 'inprogress' },
      { text: messages.commonAvailable, value: 'available' }
    );
  } else {
    statusOptions.push({ text: messages.publishEnded, value: 'ended' });
  }

  if (userHasRights) {
    statusOptions.splice(1, 0, {
      text: messages.eventCalendarAdministeredEvents,
      value: 'owner'
    });
  }

  const viewIsCalendar = !router.query.view || router.query.view === 'calendar';
  const currentType = !router.query.type ? 'current' : router.query.type;

  return (
    <div className="filters">
      <div className="filter-container">
        <div className="search">
          <Search
            onSearch={handleSearch}
            placeholder={messages.eventCalendarSearch}
            size="header"
          />
        </div>

        {user ? (
          <div className="event-status">
            <DropdownSelectable<AvailableStatus>
              category="lighter"
              defaultValue={(router.query.status || 'all') as AvailableStatus}
              dropdownTextMinWidth="120px"
              onChange={handleEventStatus}
              options={statusOptions}
              selectedValueBefore={`${messages.commonStatus}:`}
              showSelectedText={true}
              size="header"
              value={(router.query.status || 'all') as AvailableStatus}
            />
          </div>
        ) : null}

        <div className="category">
          <DropdownSelectable
            category="lighter"
            defaultValue={(router.query.category || 'all') as string}
            dropdownTextMinWidth="120px"
            onChange={handleCategory}
            options={[
              { text: messages.commonAll, value: 'all' },
              ...(data?.categories.map(({ title }) => ({
                text: title,
                value: title
              })) || [])
            ]}
            selectedValueBefore={`${messages.commonCategory}:`}
            showSelectedText={true}
            size="header"
            value={(router.query.category || 'all') as string}
          />
        </div>
      </div>

      <div className="filter-container">
        <div className="type-selector" style={{ marginRight: 10 }}>
          <Button
            active={currentType === 'current'}
            category="group"
            disabled={Boolean(router.query.search) || viewIsCalendar}
            isGroupActive={currentType === 'current'}
            onClick={() => handleTimeFrame('current')}
            size="header"
            text={messages.eventCalendarFetchCurrent}
          />

          <Button
            active={currentType === 'past'}
            category="group"
            disabled={Boolean(router.query.search) || viewIsCalendar}
            isGroupActive={currentType === 'past'}
            onClick={() => handleTimeFrame('past')}
            size="header"
            text={messages.eventCalendarFetchPast}
          />
        </div>

        <div className="view-selector">
          <Button
            active={viewIsCalendar}
            aria-label={messages.eventCalendarViewCalendar}
            category="group"
            data-tooltip={messages.eventCalendarViewCalendar}
            icon={['far', 'calendar']}
            isGroupActive={viewIsCalendar}
            onClick={() => handleView('calendar')}
            size="header"
          />

          <Button
            active={!viewIsCalendar}
            aria-label={messages.eventCalendarViewList}
            category="group"
            data-tooltip={messages.eventCalendarViewList}
            icon="list"
            isGroupActive={!viewIsCalendar}
            onClick={() => handleView('list')}
            size="header"
          />
        </div>
      </div>

      <style jsx>{`
        .filters {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: space-between;
        }
        .filter-container {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .search {
          min-width: 250px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFilters;
